import { Controller } from "@hotwired/stimulus"
import { closeOpenDropdowns } from "../lib/dropdown_state.mjs"

/**
 * Keyboard Shortcuts Controller
 *
 * Attach to the <body> to enable global keyboard shortcuts.
 *
 * Usage:
 *   <body data-controller="keyboard">
 *
 * Shortcuts:
 *   ⌘K / Ctrl+K — Open command palette (owned by search_controller)
 *   Escape      — Close any open dropdown/modal
 *   ?           — Show keyboard shortcut help (when not in an input)
 *   n           — New task
 *   g then h/p/t/c/i/s/d/r/a — Vim-style navigation
 */
export default class extends Controller {
  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    const isInput = this.isInputFocused()

    // Escape — Close open dropdowns (palette/search managed by their own controllers)
    if (event.key === "Escape") {
      if (closeOpenDropdowns(document)) event.preventDefault()
      return
    }

    // Don't trigger character shortcuts when typing in inputs
    if (isInput) return

    // ? — Show keyboard shortcuts help
    if (event.key === "?" && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      this.showShortcutsHelp()
      return
    }

    // n — New task (quick create)
    if (event.key === "n" && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault()
      window.Turbo?.visit("/tasks/new")
      return
    }

    // g then ... — Vim-style navigation (wait up to 1s for second key)
    if (event.key === "g" && !this._gPrefix) {
      this._gPrefix = true
      this._gTimeout = setTimeout(() => { this._gPrefix = false }, 1000)
      return
    }

    if (this._gPrefix) {
      clearTimeout(this._gTimeout)
      this._gPrefix = false

      const routes = {
        "h": "/dashboard",
        "p": "/projects",
        "t": "/tasks",
        "c": "/contacts",
        "i": "/invoices",
        "s": "/settings",
        "d": "/deals",
        "r": "/reports",
        "a": "/ai_assistant"
      }

      if (routes[event.key]) {
        event.preventDefault()
        window.Turbo?.visit(routes[event.key])
      }
      return
    }
  }

  isInputFocused() {
    const active = document.activeElement
    if (!active) return false
    const tag = active.tagName.toLowerCase()
    return tag === "input" || tag === "textarea" || tag === "select" || active.isContentEditable
  }

  showShortcutsHelp() {
    if (document.querySelector(".keyboard-help-overlay")) return

    const overlay = document.createElement("div")
    overlay.className = "confirm-overlay keyboard-help-overlay"
    overlay.innerHTML = `
      <div class="confirm-panel" style="max-width: 36rem;">
        <div class="confirm-body">
          <p class="confirm-title">Keyboard Shortcuts</p>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 1rem;">
            <div>
              <p style="font-size: 0.75rem; font-weight: 600; color: var(--color-mod-text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem;">Navigation</p>
              <div style="display: flex; flex-direction: column; gap: 0.375rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Search / Command palette</span>
                  <span><kbd>⌘</kbd> <kbd>K</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Dashboard</span>
                  <span><kbd>g</kbd> <kbd>h</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Projects</span>
                  <span><kbd>g</kbd> <kbd>p</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Tasks</span>
                  <span><kbd>g</kbd> <kbd>t</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Contacts</span>
                  <span><kbd>g</kbd> <kbd>c</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Invoices</span>
                  <span><kbd>g</kbd> <kbd>i</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Deals</span>
                  <span><kbd>g</kbd> <kbd>d</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Reports</span>
                  <span><kbd>g</kbd> <kbd>r</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to Settings</span>
                  <span><kbd>g</kbd> <kbd>s</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Go to AI Assistant</span>
                  <span><kbd>g</kbd> <kbd>a</kbd></span>
                </div>
              </div>
            </div>
            <div>
              <p style="font-size: 0.75rem; font-weight: 600; color: var(--color-mod-text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem;">Actions</p>
              <div style="display: flex; flex-direction: column; gap: 0.375rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">New task</span>
                  <span><kbd>n</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Close / Dismiss</span>
                  <span><kbd>Esc</kbd></span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8125rem;">
                  <span style="color: var(--color-mod-text-secondary);">Show shortcuts</span>
                  <span><kbd>?</kbd></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="confirm-actions">
          <button class="confirm-cancel" data-role="close">Close</button>
        </div>
      </div>
    `

    const close = () => {
      overlay.remove()
      document.removeEventListener("keydown", escHandler)
    }

    const escHandler = (e) => {
      if (e.key === "Escape") close()
    }

    overlay.querySelector('[data-role="close"]').addEventListener("click", close)
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    document.addEventListener("keydown", escHandler)

    document.body.appendChild(overlay)
  }
}
